import React, { useCallback, useState } from 'react';
import EDTableRow from '../../../../../../../../../BaseComponents/Table/Components/EDTableRow';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import {
  IRadBaseradAnalys,
  IRow,
} from '../../../../Queries/EkonomiskAnalysRadBaseradQuery/EkonomiskAnalysRadBaseradQuery';
import { getCurrencyFormatterParams } from '../../../../Utils/paramUtils';
import EkonomiskAnalysRadBaseradGranskning from '../../../EkonomiskAnalysRadBaseradGranskning/EkonomiskAnalysRadBaseradGranskning';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import TableCell from '../../../EkonomiskAnalysTableCell/EkonomiskAnalysTableCell';
import { firstVisibleIndex } from '../../../EkonomiskAnalysRadBaseradTable';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import EDTableCell from '../../../../../../../../../BaseComponents/Table/Components/EDTableCell';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../../../EkonomiskAnalysPage';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';

interface IProps {
  analys: IRadBaseradAnalys;
  analysSettings: IFilledLocalAnalys;
  examineIndex: number | undefined;
  impersonatedCodePrivilege: ISelectedAnalysCodePrivilege | undefined;
  updatedAnalysSettings: ILocalAnalys;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege | undefined
  ) => void;
  row: IRow;
  rowIndex: number;
  rows: IRow[];
  selectedAnalys: ISelectedAnalys;
  toggleExamine: (rowIndex: number) => void;
  valueColumnIndexes: number[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  selectedRowBG: {
    backgroundColor:
      theme.customProperties.EkonomiskAnalysRowColors.selectedRowBoxBGColor,
    '&hover': {
      backgroundColor:
        theme.customProperties.EkonomiskAnalysRowColors.selectedRowBoxBGColor,
    },
  },
  borderlessRow: {
    borderBottom: 0,
  },
  noValueRow: {
    height: theme.spacing(1.25),
  },
  granskningRow: {
    paddingTop: 0,
    paddingBottom: theme.spacing(3),
  },
  granskningScrollContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
  tableCell: {
    position: 'relative',
    '& .colPaint': {
      display: 'none',
      position: 'absolute',
      left: 0,
      width: '100%',
      backgroundColor: theme.customProperties.Fade.BlueGray.selectedLightFade,
      mixBlendMode: 'multiply',
      zIndex: 0,
      pointerEvents: 'none',
    },
    '&:hover .colPaint': {
      display: 'block',
    },
  },
  notCell: {
    width: 75,
  },
  table: {
    tableLayout: 'auto',
  },
  lastColumnCell: {
    padding: 0,
  },
  firstColumnCell: {
    maxWidth: '375px',
  },
  detailHover: {
    cursor: 'pointer',
  },
  sumDefsText: {
    margin: `0 0 ${theme.spacing(1.25)} ${theme.spacing(2)}`,
  },
  otherDefsText: {
    margin: `${theme.spacing(1.25)} 0 0 0`,
  },
}));

const RowTypeS: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes, cx } = useStyles();
  const valueColumnIndexes = props.valueColumnIndexes;
  const [hoverRowIndex, setHoverRowIndex] = useState<number>();
  const [clickedDetailRow, setClickedDetailRow] = useState<number>();

  const padding = 8 + props.row.level * 16;
  const emptyValues =
    valueColumnIndexes.length > 0 &&
    valueColumnIndexes.every(x => Number(props.row.values[x]) === 0);
  const canBeExamined = true;
  const examineOpen = props.examineIndex === props.rowIndex;

  const displayRowDefs = useCallback(
    (row: IRow) => {
      const defs = row.rowDef?.split('|');
      return (
        <>
          {defs &&
            defs.map((def, index) => {
              const row = props.analys.rows.find(
                row => row.dimRowID == Number(def)
              );

              if (row?.filtered) {
                return (
                  <EDTypography
                    key={`${index}`}
                    bold
                    className={classes.otherDefsText}
                    onClick={() => {
                      setClickedDetailRow(undefined);
                    }}
                  >
                    {row?.values[firstVisibleIndex(props.analys.columns)]}
                  </EDTypography>
                );
              }

              if (
                row?.rowType === 'B' &&
                row?.urvalId !== null &&
                !emptyValues
              ) {
                return (
                  <>
                    <EDTypography
                      key={row?.dimRowID}
                      bold
                      className={cx([
                        classes.detailHover,
                        classes.otherDefsText,
                      ])}
                      color="primary"
                      onClick={() => {
                        if (clickedDetailRow === row?.dimRowID) {
                          setClickedDetailRow(undefined);
                        } else {
                          setClickedDetailRow(row?.dimRowID);
                        }
                      }}
                    >
                      {row?.values[firstVisibleIndex(props.analys.columns)]}
                    </EDTypography>

                    {clickedDetailRow === row.dimRowID && (
                      <EkonomiskAnalysRadBaseradGranskning
                        settings={props.analysSettings}
                        updatedSettings={props.updatedAnalysSettings}
                        urvalId={row?.urvalId || ''}
                        selectedAnalys={props.selectedAnalys}
                        impersonatedCodePrivilege={
                          props.impersonatedCodePrivilege
                        }
                        updateImpersonatedCodePrivilege={
                          props.updateImpersonatedCodePrivilege
                        }
                        rowName={
                          row?.values[firstVisibleIndex(props.analys.columns)]
                        }
                      />
                    )}
                  </>
                );
              }

              if (row?.rowType === 'S') {
                return (
                  <>
                    <EDTypography
                      key={row?.dimRowID}
                      bold
                      onClick={() => {
                        if (clickedDetailRow === row?.dimRowID) {
                          setClickedDetailRow(undefined);
                        } else {
                          setClickedDetailRow(row?.dimRowID);
                        }
                      }}
                    >
                      {row?.values[firstVisibleIndex(props.analys.columns)]}
                    </EDTypography>
                    <div className={classes.sumDefsText}>
                      {displayRowDefs(row)}
                    </div>
                  </>
                );
              }

              return (
                <EDTypography
                  key={row?.dimRowID}
                  bold
                  onClick={() => {
                    setClickedDetailRow(undefined);
                  }}
                >
                  {row?.values[firstVisibleIndex(props.analys.columns)]}
                </EDTypography>
              );
            })}
        </>
      );
    },
    [
      props.analys.rows,
      props.analys.columns,
      props.analysSettings,
      props.updatedAnalysSettings,
      props.selectedAnalys,
      props.impersonatedCodePrivilege,
      props.updateImpersonatedCodePrivilege,
      emptyValues,
      classes.otherDefsText,
      classes.detailHover,
      classes.sumDefsText,
      clickedDetailRow,
      cx,
    ]
  );

  return (
    <React.Fragment key={props.row.dimRowID + props.rowIndex}>
      <EDTableRow
        hover={canBeExamined && !examineOpen}
        lightBackground
        className={cx({
          [classes.selectedRowBG]: examineOpen,
          [classes.noValueRow]: !props.row.hasData,
        })}
        onMouseEnter={() => setHoverRowIndex(props.rowIndex)}
        onMouseLeave={() => setHoverRowIndex(-1)}
      >
        {props.row.values.map((val, colIndex) => {
          if (!props.analys.columns[colIndex].visible) {
            return null;
          }

          const firstVisibleColumnIndex = firstVisibleIndex(
            props.analys.columns
          );

          const isFirst = colIndex === firstVisibleColumnIndex;

          const isNot = props.analys.columns[colIndex].fieldType === 'radnot';

          const isInverted = props.analys.columns[colIndex].inverted;

          const isPercentRow =
            props.analys.rows[props.rowIndex].values[
              firstVisibleColumnIndex
            ]?.includes('%');

          const useFormatter = !isFirst && !isNot;

          const formatter =
            isPercentRow || props.analys.columns[colIndex].isPercent
              ? findFormatter('TEDFormatIntAsPercent')
              : findFormatter('TEDFormatCurr');

          const reverseString = isInverted ? 'ReverseSign="Y";' : '';

          const formatterParams =
            isPercentRow || props.analys.columns[colIndex].isPercent
              ? reverseString + 'Decimals="1";DisplayZeroAsBlank="Y"'
              : getCurrencyFormatterParams(
                  props.analys.columns[colIndex],
                  props.updatedAnalysSettings.other.denomination,
                  props.updatedAnalysSettings.other.decimals
                );

          val =
            isPercentRow && useFormatter
              ? (
                  Number(val) /
                  (props.analysSettings.defaultSettings.denomination * 100)
                ).toString()
              : val;

          return (
            <TableCell
              key={props.analys.columns[colIndex].title1 + colIndex}
              alignment={isFirst ? 'left' : 'right'}
              paddingLeft={isFirst ? padding : undefined}
              formatter={useFormatter ? formatter : undefined}
              formatterParams={useFormatter ? formatterParams : undefined}
              value={val}
              textVariant={
                props.row.level < 3 || isFirst ? 'subtitle2' : 'body2'
              }
              onClick={() => props.toggleExamine(props.rowIndex)}
              pointer={canBeExamined}
              classes={{
                cell: examineOpen ? classes.borderlessRow : undefined,
              }}
              className={cx({
                [classes.tableCell]: !isFirst,
                [classes.notCell]: isNot,
                [classes.firstColumnCell]: isFirst,
                [classes.noValueRow]: !props.row.hasData,
              })}
              disableNoWrap={isFirst}
              hovering={
                colIndex === 4 &&
                hoverRowIndex === props.rowIndex &&
                canBeExamined
              }
              extraBold={props.row.bold}
            />
          );
        })}
        <EDTableCell
          className={cx(classes.lastColumnCell, {
            [classes.borderlessRow]: examineOpen,
          })}
        />
      </EDTableRow>
      {examineOpen && props.examineIndex !== undefined && (
        <EDTableRow>
          <EDTableCell
            colSpan={props.analys.columns.filter(x => x.visible).length + 2}
            className={cx(classes.selectedRowBG, classes.granskningRow)}
          >
            <div className={classes.granskningScrollContainer}>
              {displayRowDefs(props.row)}
            </div>
          </EDTableCell>
        </EDTableRow>
      )}
    </React.Fragment>
  );
};

export default RowTypeS;
